import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { useSyncExternalStore } from 'react';

import type { AuthenticatedUser, UserProfile } from '@/src/api/authApi';

/**
 * ── The session ─────────────────────────────────────────────────────────
 *
 * A session is two things: a token in AsyncStorage and a user in memory. This
 * module owns BOTH, because they are never independently true — a token without
 * a user is a credential for nobody, and a user without a token can't make a
 * request. Keeping them in one place is what lets [[endSession]] be a single
 * call, so no caller can clear one and forget the other.
 *
 * React reads the user through [[useSessionUser]]. Anything that isn't a
 * component — notably [[api]]'s 401 interceptor — calls the functions directly.
 *
 * ── Why this isn't a context ──
 * It was one, and the thing that broke it: the 401 interceptor has to drop the
 * session the moment the server says the credential is dead, and a module can't
 * reach into React state. Every way around that (a registered callback, an event
 * emitter) is this store with extra steps and a mount-order hazard — the
 * interceptor must work during cold-start restore, which runs BEFORE any
 * provider mounts. State at module scope exists from import time, so there is no
 * window where a 401 has nowhere to go.
 *
 * ── Why this file imports nothing of ours at runtime ──
 * Load-bearing. `api.ts` imports this module, so anything this module imports
 * back would close a ring, and a module-init cycle in Metro doesn't fail loudly —
 * it hands you an undefined import in a release build. That constraint is the
 * reason the token lives here rather than next to the HTTP client: `api.ts` is
 * downstream, so it can read the token but cannot own it.
 *
 * The `authApi` import is `import type`, erased at compile time, so it is not a
 * runtime edge (verified from the emitted JS). Keep it that way. Sentry is
 * third-party and imports nothing of ours, so it closes no ring.
 * ───────────────────────────────────────────────────────────────────────
 */

const AUTH_TOKEN_KEY = 'apiAuthToken';

/**
 * The logged-in user's identity. Note `isProfileComplete` — callers that need a
 * finished profile (booking, membership) must check it, since a logged-in user
 * isn't necessarily a complete one. The server computes it from a first name, a
 * verified email and a verified mobile.
 *
 * `fullName` is derived here, once, rather than joined at each display site. The
 * join has a rule — drop the empty half, since a last name is optional and
 * `${first} ${last}` would leave a trailing space — and that rule shouldn't be
 * re-stated (or forgotten) by every screen that prints a name. The parts stay
 * available for the places that edit them separately.
 *
 * No `token` field: that half of the session lives in AsyncStorage and is read
 * back by [[getAuthToken]]. This type is the identity, not the credential.
 */

let user: UserProfile | null = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const emit = () => {
  Sentry.setUser(user ? { id: user.id, mobile: user.mobile } : null);
  listeners.forEach((listener) => listener());
};

/**
 * The current user. Also `useSyncExternalStore`'s snapshot, which is why it
 * returns the STORED reference rather than building anything: a snapshot that
 * allocated a fresh object per call would never compare equal to the last one
 * and React would re-render until it gave up. Every derived field is computed in
 * `toSessionUser` at write time for exactly this reason.
 */
export function getSessionUser(): UserProfile | null {
  return user;
}

/**
 * The session token, straight from AsyncStorage — async, unlike the user, and
 * deliberately not mirrored in memory. [[api]]'s request transform calls this on
 * every request, so a token written here is picked up by the very next call with
 * no hydration step and no cache to go stale.
 */
export function getAuthToken(): Promise<string | null> {
  try {
    return AsyncStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return Promise.resolve(null);
  }
}

/**
 * Adopt the server's user as the session: persist the token, store the identity.
 *
 * Called at EVERY point the server returns a user + token — both verify steps —
 * rather than once at the end. `POST /auth/verify` on the MOBILE challenge is
 * what creates the account and mints the token, and the two calls after it
 * (`PATCH /me`, the email verify) are authenticated, so the token must be
 * persisted MID-FLOW; there is no way to finish signing up without it. The
 * second call is what flips `isProfileComplete` to true, and re-persisting the
 * same token (the email verify echoes it back unchanged) is a harmless no-op.
 *
 * That makes "logged in but profile incomplete" a REAL state, not one we can
 * design away: a user who quits on the details screen has an account on the
 * server and a valid token on the device.
 *
 * Token first: it's what every subsequent request needs, and if the write fails
 * (web private browsing) we must NOT report a logged-in user whose calls would
 * all 401.
 */
export async function startSession({
  token,
  ...identity
}: AuthenticatedUser): Promise<void> {
  await AsyncStorage.setItem(AUTH_TOKEN_KEY, token);
  user = identity;
  emit();
}

/**
 * Fill in the identity for a token that's ALREADY on disk — cold-start restore
 * and home's pull-to-refresh, both through [[restoreSession]], which are the only
 * legitimate callers. It exists as a separate entry point rather than a
 * `startSession` with the token re-supplied because a re-read genuinely has only
 * half a session to write: the credential is already there, only the identity is
 * new (or, at launch, missing entirely).
 *
 * Don't reach for this to log someone in: a user set without a token is a
 * session whose every request 401s. That's what [[startSession]] is for, and its
 * name is the difference.
 */
export function setRestoredUser(profile: UserProfile): void {
  user = profile;
  emit();
}

/**
 * End the session — both halves, always. The one exit, whether the user tapped
 * Log out or the server revoked us behind their back ([[api]]'s 401 handler).
 *
 * The token delete is unconditional but the notify is guarded, and the asymmetry
 * is deliberate on both sides:
 *
 *   - A null user does NOT mean there's no token. Cold-start restore runs its
 *     `GET /me` with a token while the user is still null, so a 401 there has a
 *     credential to clear and nothing to notify about. Returning early on
 *     `user === null` would strand that token on the device forever.
 *   - A screen firing six requests at once gets six 401s and calls this six
 *     times. Unguarded, that's six renders of the whole tree and — because
 *     `<Stack.Protected>` reads this — six route evaluations, for one event.
 */
export async function endSession(): Promise<void> {
  await AsyncStorage.removeItem(AUTH_TOKEN_KEY);
  if (user === null) return;
  user = null;
  emit();
}

/**
 * Subscribe a component to the session. Re-renders on sign-in, sign-out, and on
 * the 401 interceptor ending the session.
 *
 * The third argument is the server snapshot, used only while `app.config.js` has
 * `web.output: 'server'`. React calls it during SSR, where module state is per
 * request and there is no signed-in user; omitting it throws rather than
 * degrading. It costs nothing under a `'single'` (SPA) build, where React mounts
 * with `createRoot` and never asks.
 */
export function useSessionUser(): UserProfile | null {
  return useSyncExternalStore(subscribe, getSessionUser, () => null);
}
